import { DEFAULT_FORMAT_DATE } from "constants/datetime";
import { ModeForm } from "constants/enum";

import { Dispatch, SetStateAction, useEffect } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid } from "@mui/material";
import {
  DatePickerHookForm,
  DayjsLocalizationProvider,
  FormContainerControlled,
  Mail01Icon,
  TextFieldHookForm,
  Typography,
} from "components";
import { FooterFormModal } from "components/FooterFormModal/FooterFormModal";
import PhoneFieldHookForm from "components/PhoneField/PhoneField.hookform";
import TemplateSelectForm from "components/SelectApi/TemplateSelectForm";
import TemplateTypeSelectForm from "components/SelectApi/TemplateTypeSelectForm";
import UploadFilesHookForm from "components/UploadFile/UploadFiles.hookform";
import UploadFileV2HookForm from "components/UploadFile/UploadFileV2.hookform";
import { RequestCandidateEntity } from "generated";
import { dayjsTZ } from "helpers";
import {
  arrayMaxLength,
  emailRequired,
  objectRequired,
  phoneNationRequired,
  stringRequired,
} from "helpers/yup";
import { Modal } from "pages/Components";
import { useForm } from "react-hook-form";
import * as Yup from "yup";

import { RequestCandidateEntityRelation } from "./interface";

const FormSchema = Yup.object({
  candidateName: stringRequired,
  fileUrl: objectRequired,
  consentForm: arrayMaxLength(2), // eslint-disable-line
  degree: arrayMaxLength(10), // eslint-disable-line
  templateType: objectRequired,
  templateId: objectRequired,
  dueDate: objectRequired,
  dob: objectRequired,
  email: emailRequired,
  phoneNumber: phoneNationRequired,
});

interface Props {
  data?: RequestCandidateEntityRelation;
  title: string;
  subTitle: string;
  handleSubmitForm: (args: RequestCandidateEntity) => void;
  setIsModalCreateOpen: Dispatch<SetStateAction<boolean>>;
  isModalOpen: boolean;
  mode: ModeForm;
  isSuccess?: boolean;
}

export const RequestCandidateForm = (props: Props) => {
  const {
    title,
    subTitle,
    data,
    handleSubmitForm,
    setIsModalCreateOpen,
    isModalOpen,
    mode,
    isSuccess,
  } = props;

  const methods = useForm<RequestCandidateEntity | any>({
    values: {
      candidateName: data?.candidateName ?? "",
      code: data?.code ?? "",
      status: data?.status ?? "New",
      templateType: data?.template?.templateTypeId
        ? {
            value: data.template?.templateTypeId,
            label: data.template?.templateType?.name,
          }
        : null,
      templateId: data?.templateId
        ? {
            value: data.templateId,
            label: data.template?.name,
            info: data.template,
          }
        : null,
      dueDate: data?.dueDate ? dayjsTZ(data.dueDate) : null, // eslint-disable-line
      dob: data?.dateOfBirth ? dayjsTZ(data.dateOfBirth) : null, // eslint-disable-line
      email: data?.email ?? "",
      phoneNumber:
        data?.phoneNumber ??
        JSON.stringify({
          countryCode: "VN",
          countryCallingCode: "84",
          // nationalNumber: "1",
          // numberType: null,
          numberValue: "+84",
          // reason: "input",
        }),
      position: data?.position ?? "",
      candidate: data?.candidateInfo ?? "",
      obDate: data?.obDate ? dayjsTZ(data.obDate) : null, // eslint-disable-line
      fileUrl: data?.fileUrl
        ? JSON.parse(data?.fileUrl) // eslint-disable-line
        : undefined,
      consentForm: data?.consentForm
        ? JSON.parse(data?.consentForm) // eslint-disable-line
        : undefined,
      degree: data?.degree
        ? JSON.parse(data?.degree) // eslint-disable-line
        : undefined,
      description: data?.description ?? "",
    },
    resolver: yupResolver(FormSchema),
  });
  const { watch, reset, setValue, formState, resetField } = methods;
  const onSubmit = (dataSubmit: RequestCandidateEntity) => {
    handleSubmitForm(dataSubmit);
  };

  const disable = mode === ModeForm.View;

  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      if (isSuccess) {
        reset();
      }
    }
  }, [formState, reset, setValue, isSuccess]);

  return (
    <Modal
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "1300px", // Set your width here
          },
        },
      }}
      onClose={() => {
        setIsModalCreateOpen(false);
        reset();
      }}
      header={
        <>
          <Typography type="TextLG" fontWeight={600}>
            {title}
          </Typography>
          <Typography type="TextSM" fontWeight={400}>
            {subTitle}
          </Typography>
        </>
      }
      open={isModalOpen}
      body={
        <FormContainerControlled methods={methods} onSubmit={onSubmit}>
          <Box
            sx={{
              backgroundColor: "#F9FAFB",
              padding: "14px",
              borderRadius: "12px",
            }}
          >
            <Typography type="TextLG" fontWeight={600}>
              Candidate Information
            </Typography>
            <Grid container spacing="14px" marginTop="0">
              <Grid item xs={12} lg={4}>
                <TextFieldHookForm
                  required
                  name="candidateName"
                  label="Candidate Name"
                  placeholder="Enter a Name"
                  disabled={disable}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextFieldHookForm
                  name="code"
                  label="Document Check Code"
                  placeholder="Auto generate"
                  disabled
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextFieldHookForm
                  name="status"
                  label="Status request allocation"
                  placeholder="Auto generate"
                  disabled
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TemplateTypeSelectForm
                  require
                  name="templateType"
                  label="Template Type"
                  disabled={disable}
                  onChange={(e) => {
                    setValue("templateId", null);
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TemplateSelectForm
                  require
                  name="templateId"
                  label="Template"
                  disabled={disable}
                  templateTypeId={watch("templateType")?.value ?? null}
                  onChange={(e) => {
                    if (e) {
                      if (e?.info?.templateType) {
                        setValue("templateType", {
                          value: e?.info?.templateType?.id,
                          label: e?.info?.templateType?.name,
                        });
                      } else {
                        setValue("templateType", null);
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <DayjsLocalizationProvider>
                  <DatePickerHookForm
                    required
                    name="dueDate"
                    label="Due Date"
                    placeholder="Select date"
                    format={DEFAULT_FORMAT_DATE}
                    disabled={disable}
                  />
                </DayjsLocalizationProvider>
              </Grid>
              <Grid item xs={12} lg={4}>
                <DayjsLocalizationProvider>
                  <DatePickerHookForm
                    required
                    name="dob"
                    label="DOB"
                    placeholder="Select date"
                    format={DEFAULT_FORMAT_DATE}
                    maxDate={dayjsTZ(
                      new Date().setFullYear(new Date().getFullYear() + 15)
                    )}
                    minDate={dayjsTZ(
                      new Date().setFullYear(new Date().getFullYear() - 100)
                    )}
                    disabled={disable}
                  />
                </DayjsLocalizationProvider>
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextFieldHookForm
                  required
                  InputProps={{
                    startAdornment: (
                      <Mail01Icon fontSize="small" sx={{ color: "#667085" }} />
                    ),
                  }}
                  name="email"
                  label="Email"
                  placeholder="Enter email"
                  disabled={disable}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <PhoneFieldHookForm
                  required
                  name="phoneNumber"
                  label="Phone number"
                  placeholder="Enter phone number"
                  disabled={disable}
                />
                {/* <PhoneHookForm
                  name="phoneNumber"
                  label="Phone number"
                  disabled={disable}
                /> */}
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextFieldHookForm
                  name="position"
                  label="Position"
                  placeholder="Enter info"
                  disabled={disable}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextFieldHookForm
                  name="candidate"
                  label="Working Location"
                  placeholder="Enter info"
                  disabled={disable}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <DayjsLocalizationProvider>
                  <DatePickerHookForm
                    name="obDate"
                    label="OB Date"
                    placeholder="Select a date"
                    format={DEFAULT_FORMAT_DATE}
                    disabled={disable}
                  />
                </DayjsLocalizationProvider>
              </Grid>
              <Grid item xs={12} lg={12}>
                <TextFieldHookForm
                  name="description"
                  label="Remark"
                  placeholder="e.g. Explaination for this field"
                  multiline
                  rows={4}
                  disabled={disable}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <UploadFileV2HookForm
                  required
                  name="fileUrl"
                  label="Candidate's CV"
                  disabled={disable}
                  isViewForm={disable}
                  fileType={["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG"]}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <UploadFilesHookForm
                  name="consentForm"
                  label="Candidate's Consent form"
                  disabled={disable}
                  isViewForm={disable}
                  maxFile={2}
                  fileType={["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG"]}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <UploadFilesHookForm
                  name="degree"
                  label="Candidate's degrees"
                  disabled={disable}
                  isViewForm={disable}
                  maxFile={10}
                  fileType={["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG"]}
                />
              </Grid>
            </Grid>
          </Box>
          <FooterFormModal
            mode={mode}
            reset={reset}
            setIsModalCreateOpen={setIsModalCreateOpen}
          />
        </FormContainerControlled>
      }
    />
  );
};
